import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabaseClient';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  CheckCircle2, AlertTriangle,
  Building2, DollarSign, FileText, Calendar,
  ChevronRight, Loader2, HelpCircle, Info
} from 'lucide-react';
import { calculateComplianceThresholds } from '@/utils/complianceCalculator';

const CompliancePage = () => {
  const navigate = useNavigate();
  const { currentOrg, user, userData } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState(null);
  const [latestAssessment, setLatestAssessment] = useState(null);

  const [formData, setFormData] = useState({
    consolidated_revenue: '',
    consolidated_assets: '',
    employee_count: '',
    has_nger_obligations: false,
    nger_registration_number: '',
    is_scheme_or_super: false,
    scheme_asset_value: ''
  });

  useEffect(() => {
    if (currentOrg) {
      fetchLatestAssessment();
    }
  }, [currentOrg?.id]);


  const fetchLatestAssessment = async () => {
    if (!currentOrg) return;
    try {
      const { data: assessment } = await supabase
        .from('compliance_assessments')
        .select('*')
        .eq('organisation_id', currentOrg.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (assessment) {
        setLatestAssessment(assessment);
      }
    } catch (error) {
      console.error('Error fetching assessment:', error);
    }
  };

  const handleCheck = async () => {
    if (!currentOrg) {
      toast.error('Organisation not loaded');
      return;
    }
    setChecking(true);
    try {
      if (!userData) {
        toast.error('User not found');
        setChecking(false);
        return;
      }

      // Calculate compliance thresholds client-side
      const complianceResult = calculateComplianceThresholds({
        consolidated_revenue: formData.consolidated_revenue ? parseFloat(formData.consolidated_revenue) : null,
        consolidated_assets: formData.consolidated_assets ? parseFloat(formData.consolidated_assets) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        has_nger_obligations: formData.has_nger_obligations,
        nger_registration_number: formData.nger_registration_number || null,
        is_scheme_or_super: formData.is_scheme_or_super,
        scheme_asset_value: formData.scheme_asset_value ? parseFloat(formData.scheme_asset_value) : null
      });

      // Try to save assessment to Supabase (may fail if RLS not configured)
      const assessmentData = {
        organisation_id: currentOrg.id,
        consolidated_revenue: formData.consolidated_revenue ? parseFloat(formData.consolidated_revenue) : null,
        consolidated_assets: formData.consolidated_assets ? parseFloat(formData.consolidated_assets) : null,
        employee_count: formData.employee_count ? parseInt(formData.employee_count) : null,
        meets_size_threshold: complianceResult.meets_size_threshold,
        has_nger_obligations: formData.has_nger_obligations,
        nger_registration_number: formData.nger_registration_number || null,
        is_scheme_or_super: formData.is_scheme_or_super,
        scheme_asset_value: formData.scheme_asset_value ? parseFloat(formData.scheme_asset_value) : null,
        meets_scheme_threshold: complianceResult.meets_scheme_threshold,
        is_reporting_entity: complianceResult.is_reporting_entity,
        compliance_group: complianceResult.compliance_group,
        compliance_start_date: complianceResult.compliance_start_date,
        created_by: userData.id
      };

      const { error: insertError } = await supabase
        .from('compliance_assessments')
        .insert(assessmentData);

      if (insertError) {
        console.warn('Could not save assessment to database:', insertError);
        // Continue showing results - calculation still works
      } else {
        await fetchLatestAssessment();
      }

      // Always show the result (calculation is client-side)
      setResult(complianceResult);
      toast.success('Compliance assessment complete');
    } catch (error) {
      console.error('Error checking compliance:', error);
      toast.error('Failed to check compliance');
    } finally {
      setChecking(false);
    }
  };

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 }).format(value);
  };

  const getGroupBadge = (group) => {
    const badges = {
      'group_1': { label: 'Group 1', color: 'bg-red-100 text-red-800', desc: 'Largest entities' },
      'group_2': { label: 'Group 2', color: 'bg-amber-100 text-amber-800', desc: 'Large entities' },
      'group_3': { label: 'Group 3', color: 'bg-blue-100 text-blue-800', desc: 'Medium entities' }
    };
    return badges[group] || { label: 'Exempt', color: 'bg-green-100 text-green-800', desc: 'Not required' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#003B5C]" />
      </div>
    );
  }

  return (
    <div className="bg-[#F8F9FA] min-h-[calc(100vh-56px)]">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#003B5C] flex items-center" style={{ fontFamily: 'Outfit, sans-serif' }}>
              <FileText className="w-7 h-7 mr-3 text-[#74A57F]" />
              AASB S2 Compliance
            </h1>
            <p className="text-slate-600 mt-1">
              {currentOrg?.name} • Determine your mandatory reporting obligations
            </p>
          </div>
        </div>
        {/* Info Banner */}
        <div className="bg-blue-50 rounded-2xl p-6 border border-blue-100 mb-8">
          <div className="flex items-start space-x-4">
            <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Info className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">Mandatory ESG Reporting in Australia</h3>
              <p className="text-sm text-blue-800 mb-3">
                From 1 January 2025, certain Australian entities must prepare sustainability reports
                under AASB S2 as part of their annual financial reporting. This calculator helps determine
                if your organisation meets the mandatory reporting thresholds.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                <div className="bg-white/60 rounded-lg p-2">
                  <span className="font-semibold text-red-700">Group 1:</span> FY starting 1 Jan 2025
                </div>
                <div className="bg-white/60 rounded-lg p-2">
                  <span className="font-semibold text-amber-700">Group 2:</span> FY starting 1 Jul 2026
                </div>
                <div className="bg-white/60 rounded-lg p-2">
                  <span className="font-semibold text-blue-700">Group 3:</span> FY starting 1 Jul 2027
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Previous Assessment */}
        {latestAssessment && !result && (
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 mb-8">
            <h3 className="font-semibold text-[#003B5C] mb-4">Previous Assessment</h3>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {latestAssessment.is_reporting_entity ? (
                  <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                    <AlertTriangle className="w-6 h-6 text-amber-600" />
                  </div>
                ) : (
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-slate-800">
                    {latestAssessment.is_reporting_entity ? 'Reporting Required' : 'Not Currently Required'}
                  </p>
                  <p className="text-sm text-slate-500">
                    Assessed on {new Date(latestAssessment.assessment_date).toLocaleDateString('en-AU')}
                  </p>
                </div>
              </div>
              {latestAssessment.compliance_group && (
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGroupBadge(latestAssessment.compliance_group).color}`}>
                  {getGroupBadge(latestAssessment.compliance_group).label}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Assessment Form */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="p-6 border-b border-slate-100">
            <h2 className="text-lg font-semibold text-[#003B5C]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Check Your Reporting Obligations
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              Enter your organisation's details to determine if you meet mandatory reporting thresholds
            </p>
          </div>

          <div className="p-6 space-y-8">
            {/* Threshold 1: Size */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <Building2 className="w-5 h-5 text-[#003B5C]" />
                <h3 className="font-semibold text-[#003B5C]">Threshold 1: Entity Size</h3>
                <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Need 2 of 3</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label className="text-slate-700 flex items-center">
                    Consolidated Revenue
                    <HelpCircle className="w-3 h-3 ml-1 text-slate-400" />
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      placeholder="50,000,000"
                      value={formData.consolidated_revenue}
                      onChange={(e) => setFormData({ ...formData, consolidated_revenue: e.target.value })}
                      className="pl-7"
                      data-testid="revenue-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Threshold: ≥$50M</p>
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center">
                    Consolidated Assets
                    <HelpCircle className="w-3 h-3 ml-1 text-slate-400" />
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      placeholder="25,000,000"
                      value={formData.consolidated_assets}
                      onChange={(e) => setFormData({ ...formData, consolidated_assets: e.target.value })}
                      className="pl-7"
                      data-testid="assets-input"
                    />
                  </div>
                  <p className="text-xs text-slate-500 mt-1">Threshold: ≥$25M</p>
                </div>
                <div>
                  <Label className="text-slate-700 flex items-center">
                    Employee Count
                    <HelpCircle className="w-3 h-3 ml-1 text-slate-400" />
                  </Label>
                  <Input
                    type="number"
                    placeholder="100"
                    value={formData.employee_count}
                    onChange={(e) => setFormData({ ...formData, employee_count: e.target.value })}
                    className="mt-1"
                    data-testid="employee-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">Threshold: ≥100</p>
                </div>
              </div>
            </div>

            {/* Threshold 2: NGER */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="w-5 h-5 text-[#003B5C]" />
                <h3 className="font-semibold text-[#003B5C]">Threshold 2: NGER Obligations</h3>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.has_nger_obligations}
                    onChange={(e) => setFormData({ ...formData, has_nger_obligations: e.target.checked })}
                    className="mt-1 h-4 w-4 text-[#003B5C] rounded"
                    data-testid="nger-checkbox"
                  />
                  <div>
                    <span className="font-medium text-slate-800">
                      We have National Greenhouse and Energy Reporting (NGER) obligations
                    </span>
                    <p className="text-sm text-slate-500 mt-1">
                      Entities registered under the NGER Act 2007 are automatically included in mandatory reporting
                    </p>
                  </div>
                </label>
                {formData.has_nger_obligations && (
                  <div className="mt-4">
                    <Label className="text-slate-700">NGER Registration Number (optional)</Label>
                    <Input
                      type="text"
                      placeholder="e.g., NGER-12345"
                      value={formData.nger_registration_number}
                      onChange={(e) => setFormData({ ...formData, nger_registration_number: e.target.value })}
                      className="mt-1 max-w-xs"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Threshold 3: Schemes */}
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <DollarSign className="w-5 h-5 text-[#003B5C]" />
                <h3 className="font-semibold text-[#003B5C]">Threshold 3: Registered Schemes</h3>
              </div>
              <div className="bg-slate-50 rounded-xl p-4">
                <label className="flex items-start space-x-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_scheme_or_super}
                    onChange={(e) => setFormData({ ...formData, is_scheme_or_super: e.target.checked })}
                    className="mt-1 h-4 w-4 text-[#003B5C] rounded"
                    data-testid="scheme-checkbox"
                  />
                  <div>
                    <span className="font-medium text-slate-800">
                      We are a registered scheme, registrable superannuation entity, or retail CCIV
                    </span>
                    <p className="text-sm text-slate-500 mt-1">
                      These entities must report if assets are ≥$5 billion
                    </p>
                  </div>
                </label>
                {formData.is_scheme_or_super && (
                  <div className="mt-4">
                    <Label className="text-slate-700">Total Asset Value</Label>
                    <div className="relative mt-1">
                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                      <Input
                        type="number"
                        placeholder="5,000,000,000"
                        value={formData.scheme_asset_value}
                        onChange={(e) => setFormData({ ...formData, scheme_asset_value: e.target.value })}
                        className="pl-7 max-w-xs"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-1">Threshold: ≥$5B</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Action Button */}
          <div className="p-6 bg-slate-50 border-t border-slate-100">
            <Button
              onClick={handleCheck}
              disabled={checking}
              className="w-full bg-[#003B5C] hover:bg-[#002A42] text-white h-12"
              data-testid="check-compliance-btn"
            >
              {checking ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Checking...
                </>
              ) : (
                <>
                  Check Compliance Status
                  <ChevronRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Result */}
        {result && (
          <div className={`mt-8 rounded-2xl p-6 border ${result.is_reporting_entity
            ? 'bg-amber-50 border-amber-200'
            : 'bg-green-50 border-green-200'
            }`} data-testid="compliance-result">
            <div className="flex items-start space-x-4">
              {result.is_reporting_entity ? (
                <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
              ) : (
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-6 h-6 text-green-600" />
                </div>
              )}
              <div className="flex-1">
                <div className="flex items-center justify-between mb-2">
                  <h3 className={`text-lg font-bold ${result.is_reporting_entity ? 'text-amber-900' : 'text-green-900'}`}>
                    {result.is_reporting_entity ? 'Mandatory Reporting Required' : 'Not Currently Required to Report'}
                  </h3>
                  {result.compliance_group && (
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${getGroupBadge(result.compliance_group).color}`}>
                      {getGroupBadge(result.compliance_group).label} - {getGroupBadge(result.compliance_group).desc}
                    </span>
                  )}
                </div>

                {result.thresholds_met.length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-slate-700 mb-2">Thresholds Met:</p>
                    <ul className="space-y-1">
                      {result.thresholds_met.map((threshold, idx) => (
                        <li key={idx} className="flex items-center text-sm text-slate-600">
                          <CheckCircle2 className="w-4 h-4 text-amber-500 mr-2" />
                          {threshold}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {result.compliance_start_date && (
                  <div className="flex items-center space-x-2 mb-4 p-3 bg-white/60 rounded-lg">
                    <Calendar className="w-5 h-5 text-[#003B5C]" />
                    <span className="text-sm">
                      <strong>Compliance Start Date:</strong> Financial years beginning on or after{' '}
                      <span className="font-bold text-[#003B5C]">
                        {new Date(result.compliance_start_date).toLocaleDateString('en-AU', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </span>
                  </div>
                )}

                <p className={`text-sm ${result.is_reporting_entity ? 'text-amber-800' : 'text-green-800'}`}>
                  {result.recommendation}
                </p>

                {result.is_reporting_entity && (
                  <div className="mt-4 flex space-x-3">
                    <Button
                      onClick={() => navigate('/dashboard')}
                      className="bg-[#003B5C] hover:bg-[#002A42] text-white"
                    >
                      Start Tracking Emissions
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => navigate('/risks')}
                    >
                      Set Up Climate Risk Register
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default CompliancePage;
